import clsx from "clsx";
import { Fragment, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

import {
  ClickEvent,
  Menu,
  MenuDivider,
  MenuItem,
  MenuItemKind,
  MenuProps,
} from "mds/components/Menu";
import { SquareButton, type SquareButtonProps } from "mds/components/buttons/SquareButton";
import { ChevronDownIcon } from "mds/icons";
import { ComponentEventPropertiesWithKey } from "utils/amplitude";

export type DropdownItemType = {
  id: string;
  title: string | ReactNode;
  iconStart?: ReactNode;
  addDivider?: boolean;
};

export type DropdownMenuProps<
  T extends DropdownItemType,
  V extends T[keyof T],
> = ComponentEventPropertiesWithKey & {
  items: T[];
  selectedValue?: V;
  kind?: SquareButtonProps["kind"];
  size?: SquareButtonProps["size"];
  className?: string;
  buttonText?: ReactNode;
  onSelectionChange: (selectedItem: T) => void;
  disabled?: boolean;
  truncateWidth?: "xs" | "s" | "m" | "l" | "xl" | "xxl" | `max-w-${string}`;
  allowFullLengthItems?: boolean;
  title?: string;
  menuProps?: Partial<MenuProps>;
  menuItemKind?: MenuItemKind;
  openMenuEventName?: string;
};

export const DropdownMenu = <T extends DropdownItemType, V extends T[keyof T]>({
  selectedValue,
  className,
  menuItemKind,
  kind = "tertiary",
  size = "xs",
  truncateWidth = "m",
  allowFullLengthItems = false,
  items,
  buttonText,
  onSelectionChange,
  disabled,
  title,
  eventName,
  eventProperties,
  eventPropertyKey,
  openMenuEventName,
  menuProps,
}: DropdownMenuProps<T, V>) => {
  const truncateWidthClass = truncateWidth
    ? truncateWidth.startsWith("max-w-")
      ? truncateWidth
      : `max-w-${truncateWidth}`
    : "";

  const selectedItem = items.find((item) => item.id === selectedValue) || items[0];

  const onItemClick = (e: ClickEvent) => {
    const item = e.value as T;
    onSelectionChange(item);
  };

  const menuButton = (
    <SquareButton
      className={twMerge(className, truncateWidthClass)}
      disabled={disabled}
      eventName={openMenuEventName}
      kind={kind}
      size={size}
      title={title}
    >
      {!buttonText && selectedItem?.iconStart}
      <span className={clsx("min-w-0 truncate", truncateWidthClass)}>
        {buttonText || selectedItem?.title}
      </span>
      <ChevronDownIcon />
    </SquareButton>
  );

  const MenuItems = items.map((item) => {
    const selected = item.id === selectedValue;
    return (
      <Fragment key={item.id}>
        <MenuItem
          checked={selected}
          className={clsx({ selected })}
          disabled={"disabled" in item ? (item.disabled as boolean) : false}
          eventName={eventName}
          eventProps={{
            ...eventProperties,
            [eventPropertyKey || "itemId"]: item.id,
          }}
          kind={menuItemKind}
          title={typeof item.title === "string" ? item.title : undefined}
          value={item}
          onClick={onItemClick}
        >
          {item.iconStart}
          <span
            className={clsx(
              truncateWidth && !allowFullLengthItems && `block truncate ${truncateWidthClass}`,
            )}
          >
            {item.title}
          </span>
        </MenuItem>
        {item.addDivider && <MenuDivider />}
      </Fragment>
    );
  });

  return (
    <Menu menuButton={menuButton} overflow="auto" {...menuProps}>
      {MenuItems}
    </Menu>
  );
};
